// src/services/productionAdminService.ts
// The studio side of StrymHub Studios productions: creating them, entering credits,
// and attaching the films they produced.
//
// All of this existed as ADMIN-only endpoints with no screen, which is why credits on
// the player had to be inserted by hand.

import { buildQuery, studioRequest } from './studioApi';
import type {
  PagedResult,
  Production,
  ProductionSummary,
} from '../types/studio';

/**
 * Credits reach a player page only when every one of these holds. The server joins
 * credits to content through ProductionWork and filters on the production being PUBLIC,
 * so a break anywhere in the chain shows nothing and says nothing.
 */
export const CREDIT_CHAIN = [
  'the production exists',
  'its visibility is Public',
  'the film is attached to it as a work',
  'the credits are on the production',
] as const;

/** Mirrors ProductionCredit.Department. Order is end-roll order, as the server groups it. */
export const CREDIT_DEPARTMENTS: { value: string; label: string }[] = [
  { value: 'CAST', label: 'Cast' },
  { value: 'DIRECTING', label: 'Directing' },
  { value: 'WRITING', label: 'Writing' },
  { value: 'PRODUCING', label: 'Producing' },
  { value: 'CAMERA', label: 'Camera' },
  { value: 'SOUND', label: 'Sound' },
  { value: 'MUSIC', label: 'Music' },
  { value: 'EDITING', label: 'Editing' },
  { value: 'ART', label: 'Art' },
  { value: 'COSTUME', label: 'Costume' },
  { value: 'HAIR_MAKEUP', label: 'Hair & make-up' },
  { value: 'VFX_ANIMATION', label: 'VFX & animation' },
  { value: 'STUNTS', label: 'Stunts' },
  { value: 'CASTING', label: 'Casting' },
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'OTHER', label: 'Other' },
];

/** Mirrors ProductionWork.WorkRole. */
export const WORK_ROLES: { value: string; label: string }[] = [
  { value: 'MAIN', label: 'The film itself' },
  { value: 'TRAILER', label: 'Trailer' },
  { value: 'BEHIND_THE_SCENES', label: 'Behind the scenes' },
  { value: 'CLIP', label: 'Clip' },
  { value: 'PODCAST', label: 'Podcast' },
  { value: 'INTERVIEW', label: 'Interview' },
  { value: 'EXTRA', label: 'Extra' },
];

/**
 * Mirrors ProductionRequest. Every field is sent on every save, because PUT replaces the
 * whole record - an omitted field is reset, not left alone.
 */
export interface ProductionInput {
  title: string;
  logline?: string;
  synopsis?: string;
  format?: string;
  stage?: string;
  visibility?: string;
  genre?: string;
  posterUrl?: string;
  bannerUrl?: string;
  trailerUrl?: string;
  shootLocation?: string;
  shootStartDate?: string;
  shootEndDate?: string;
  releaseDate?: string;
  productionYear?: number | null;
  runtimeMinutes?: number | null;
  partnerName?: string;
  partnerUrl?: string;
  featured?: boolean;
  openForCasting?: boolean;
}

export interface CreditInput {
  creditedName: string;
  roleName: string;
  department?: string;
  /** Optional - links the credit to an existing member's profile page. */
  memberSlug?: string;
  lead?: boolean;
  displayOrder?: number;
  note?: string;
}

export interface WorkInput {
  contentId: string;
  role?: string;
  displayOrder?: number;
}

const base = '/productions';
const slugPath = (slug: string) => `${base}/${encodeURIComponent(slug)}`;

export const productionAdminService = {
  /** Studio listing - includes drafts, which the public listing never does. */
  listAll(page = 0, size = 48): Promise<PagedResult<ProductionSummary>> {
    return studioRequest<PagedResult<ProductionSummary>>(
      `${base}/studio/all${buildQuery({ page, size })}`,
    );
  },

  get(slug: string): Promise<Production> {
    return studioRequest<Production>(`${base}/studio/${encodeURIComponent(slug)}`);
  },

  create(input: ProductionInput): Promise<Production> {
    return studioRequest<Production>(base, { method: 'POST', body: input });
  },

  update(slug: string, input: ProductionInput): Promise<Production> {
    return studioRequest<Production>(slugPath(slug), { method: 'PUT', body: input });
  },

  remove(slug: string): Promise<{ slug: string }> {
    return studioRequest<{ slug: string }>(slugPath(slug), { method: 'DELETE' });
  },

  addCredit(slug: string, input: CreditInput): Promise<Production> {
    return studioRequest<Production>(`${slugPath(slug)}/credits`, { method: 'POST', body: input });
  },

  removeCredit(slug: string, creditId: string): Promise<Production> {
    return studioRequest<Production>(
      `${slugPath(slug)}/credits/${encodeURIComponent(creditId)}`,
      { method: 'DELETE' },
    );
  },

  attachWork(slug: string, input: WorkInput): Promise<Production> {
    return studioRequest<Production>(`${slugPath(slug)}/works`, { method: 'POST', body: input });
  },

  detachWork(slug: string, workId: string): Promise<Production> {
    return studioRequest<Production>(
      `${slugPath(slug)}/works/${encodeURIComponent(workId)}`,
      { method: 'DELETE' },
    );
  },
};

export default productionAdminService;
